import { motion } from "framer-motion";
import { Bot, BookOpen, GraduationCap, BarChart3, Users, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import platformMockup from "@/assets/platform-mockup.jpg";

const features = [
  {
    icon: Bot,
    title: "Soul Safe Counsel",
    description: "Process what happened confidentially. No judgment, no gossip, no fear. Available 24/7.",
    iconColor: "text-accent",
    iconBg: "bg-accent/15",
  },
  {
    icon: BookOpen,
    title: "On-Demand Recovery Path",
    description: "Structured healing anytime, anywhere, on any device. Move from confusion to clarity at your own pace.",
    iconColor: "text-accent",
    iconBg: "bg-accent/15",
  },
  {
    icon: GraduationCap,
    title: "Certification & Credentials",
    description: "Get grounded in truth so you're equipped to lead, advocate, and protect, with real credentials.",
    iconColor: "text-accent",
    iconBg: "bg-accent/15",
  },
];

export function ProductVisualization() {
  const navigate = useNavigate();

  return (
    <section id="platform-tools" className="py-28 md:py-36 bg-background">
      <div className="container px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-3xl mx-auto text-center mb-8"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent mb-6">
            Your Complete Healing Toolkit
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
            Why Choose
            <br />
            Soul Safe Sanctuary?
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
            Soul Safe Sanctuary is more than just a platform. We're a{" "}
            <strong className="text-foreground">mission-driven movement</strong>,
            dedicated to providing{" "}
            <strong className="text-foreground">biblically grounded, trauma-informed</strong>{" "}
            support that equips you to heal and lead with confidence.
          </p>
        </motion.div>

        {/* Two-column: Features left, Mockup right */}
        <div className="max-w-6xl mx-auto mt-20">
          <motion.h3
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-serif text-3xl md:text-4xl lg:text-5xl font-black text-foreground mb-12" style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Here's What You Can Expect:
          </motion.h3>

          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Feature list */}
            <div className="space-y-10">
              {features.map((feature, i) => {
                const Icon = feature.icon;
                return (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                    className="flex gap-5"
                  >
                    <div className={`w-14 h-14 rounded-xl ${feature.iconBg} flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-7 h-7 ${feature.iconColor}`} />
                    </div>
                    <div>
                      <h4 className="font-serif text-xl md:text-2xl font-bold text-foreground mb-2">
                        {feature.title}
                      </h4>
                      <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Device mockup */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="relative"
            >
              <img
                src={platformMockup}
                alt="Soul Safe Sanctuary platform on laptop and mobile"
                className="w-full rounded-xl shadow-[var(--shadow-elevated)]"
              />
            </motion.div>
          </div>
        </div>

      </div>
    </section>
  );
}
